import React, {Fragment} from 'react';

import {Button, Stack} from '@mui/material';
import {
	AlertInfo,
	EmptySearchState,
	ErrorState,
	MainFooter,
	SentinelLoading,
} from 'presentation/components';
import {PageWrapperLayout} from 'presentation/components/PageWrapperLayout';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {Placement} from 'react-joyride';
import Tutorial from 'presentation/components/Tutorial';
import {HeaderFilter} from '../../components/HeaderFilter';
import {Protocols} from '../../components/Protocols';
import {useConsultProtocols, useFilterByDatePicker, useFilters} from '../../hooks';
import {StyledFooterContainer} from './styles';

const {noResults, failToLoadTitle, failToLoadDescription} = translations['pt-br'].commons;
const {
	title,
	emptySearch: {
		noProtocolsFoundByDateDescription,
		noProtocolsFoundByDateButton,
		noProtocolsFoundByFilterButton,
		noProtocolsFoundByFilterDescription,
	},
	alertRangeDate,
	tour: {tourDescription},
} = translations['pt-br'].privateFlow.consultProtocols;

export function ProtocolsConsult() {
	const {isMobile} = useMediaQueries();

	const {openModalExternally, openModalDatePickerState, handleClearDates} =
		useFilterByDatePicker();

	const {getSelectedFiltersCount, toggleDrawer, openDrawerState, handleClearFilters} =
		useFilters();

	const {
		isProtocolsListLoading,
		isProtocolsListEmpty,
		isProtocolsHasError,
		loadingNewPageState,
		refetch,
		consultProtocolsResponses,
		consultProtocolsFilters,
		items,
		scrollableContentRef,
		filterConsultProtocols,
		handleVisible,
	} = useConsultProtocols();

	const handleError = () => {
		handleClearFilters();
		handleClearDates();
		refetch();
	};

	const showEmptyState =
		items.length === 0 &&
		(isProtocolsListEmpty || !isProtocolsHasError) &&
		!isProtocolsListLoading;

	const {SEARCH_PARAMS} = useQueryParams();
	const {protocolType, status} = SEARCH_PARAMS;

	const totalItems =
		getSelectedFiltersCount() === 0 || (!protocolType && !status)
			? consultProtocolsResponses.length
			: filterConsultProtocols(consultProtocolsResponses).length;

	return (
		<Fragment>
			<PageWrapperLayout title={title}>
				{!isProtocolsListLoading && !isProtocolsHasError ? (
					<Fragment>
						<HeaderFilter
							openModalDatePicker={openModalDatePickerState}
							openModalExternally={() =>
								openModalExternally(!openModalDatePickerState)
							}
							openDrawerExternallyState={openDrawerState}
							openDrawerExternally={() => toggleDrawer(!openDrawerState)}
							otherData={consultProtocolsFilters}
						/>

						<AlertInfo.Root
							sx={{
								mt: 2,
								marginX: isMobile ? 2 : 4,
							}}>
							<AlertInfo.Description>{alertRangeDate}</AlertInfo.Description>
						</AlertInfo.Root>
					</Fragment>
				) : null}
				{items ? (
					<Stack ref={scrollableContentRef}>
						{!isProtocolsListLoading ? (
							<Protocols
								protocols={items}
								shouldShowTotalItems={items.length > 0 && !isProtocolsListLoading}
								totalItems={totalItems}
							/>
						) : null}
						<SentinelLoading
							minHeight="200px"
							onVisible={handleVisible}
							isLoading={isProtocolsListLoading || loadingNewPageState}
						/>
					</Stack>
				) : null}
				{showEmptyState ? (
					<Stack sx={{height: '100%', p: 2}} alignItems="center" justifyContent="center">
						<EmptySearchState
							title={noResults}
							description={
								getSelectedFiltersCount() >= 2
									? noProtocolsFoundByFilterDescription
									: noProtocolsFoundByDateDescription
							}
							dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROTOCOLO_ENCONTRADO}>
							<Button
								variant="contained"
								color="primary"
								size="large"
								onClick={() => {
									if (getSelectedFiltersCount() >= 2) {
										toggleDrawer(true);
									} else {
										openModalExternally(true);
									}
								}}>
								{getSelectedFiltersCount() >= 2
									? noProtocolsFoundByFilterButton
									: noProtocolsFoundByDateButton}
							</Button>
						</EmptySearchState>
					</Stack>
				) : null}
				{isProtocolsHasError ? (
					<ErrorState
						title={failToLoadTitle}
						description={failToLoadDescription}
						handleRefresh={handleError}
					/>
				) : null}
				<StyledFooterContainer>
					<MainFooter customMarginBottom={'48px'} />
				</StyledFooterContainer>
			</PageWrapperLayout>
			{!isProtocolsHasError && !isProtocolsListLoading ? (
				<Tutorial
					steps={[
						{
							target: '#header',
							content: tourDescription,
							placement: 'bottom-start' as Placement,
							disableBeacon: true,
						},
					]}
					storageKey="consultProtocolsTutorial"
				/>
			) : null}
		</Fragment>
	);
}
